// DynamicEditForm.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./DynamicEditForm.css";

const DynamicEditForm = ({ fields, apiUrl }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { id } = useParams();

  /* -----------------------------
   * Fetch single record
   * --------------------------- */
  useEffect(() => {
    let mounted = true;
    const fetchProductData = async () => {
      try {
        const response = await axios.get(`${apiUrl}/${id}`);
        if (!mounted) return;
        setFormData(response.data || {});
      } catch (error) {
        console.error("Error fetching product data:", error);
        setErrorMessage(
          error?.response?.data?.message || "Failed to load data."
        );
      } finally {
        if (mounted) setFetching(false);
      }
    };
    fetchProductData();
    return () => {
      mounted = false;
    };
  }, [apiUrl, id]);

  /* -----------------------------
   * Helpers
   * --------------------------- */
  const toNumberArray = (v) => {
    if (typeof v === "string") {
      return v
        .split(",")
        .map((s) => Number(s.trim()))
        .filter(Number.isFinite);
    }
    if (Array.isArray(v)) {
      return v
        .flatMap((item) =>
          typeof item === "string"
            ? item.split(",").map((t) => t.trim())
            : [item]
        )
        .map(Number)
        .filter(Number.isFinite);
    }
    return [];
  };

  const buildCleanPayload = (raw) => {
    const out = { ...raw };

    // Remove immutable/system fields if present
    delete out._id;
    delete out.__v;
    delete out.createdAt;
    delete out.updatedAt;

    // Normalize id (Number)
    if (out.id !== undefined) out.id = Number(out.id);

    // Normalize projects → [Number]
    // NOTE: The input may be CSV string (e.g., "143,147,150") or an array.
    out.projects = toNumberArray(out.projects);

    // Clean nested photo array → [{ image }]
    if (Array.isArray(out.photo)) {
      out.photo = out.photo.map((p) => ({ image: p?.image ?? "" }));
    }

    return out;
  };

  /* -----------------------------
   * Change Handlers
   * --------------------------- */
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    // If this is the "projects" field, keep CSV in state (string).
    // We'll convert to [Number] in buildCleanPayload on submit.
    if (name === "projects") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    // Optional: if field is strictly numeric (like 'id'), allow numbers
    if (name === "id" && type === "number") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "" ? "" : Number(value),
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (fieldName, index, subField, value) => {
    const updatedArray = [...(formData[fieldName] || [])];
    if (!updatedArray[index]) updatedArray[index] = {};
    updatedArray[index][subField] = value;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: updatedArray,
    }));
  };

  const handleAddItem = (fieldName) => {
    const updatedArray = [...(formData[fieldName] || []), {}];
    setFormData((prev) => ({
      ...prev,
      [fieldName]: updatedArray,
    }));
  };

  const handleRemoveItem = (fieldName, index) => {
    const updatedArray = [...(formData[fieldName] || [])];
    updatedArray.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: updatedArray,
    }));
  };

  /* -----------------------------
   * Submit (Ctrl+S supported)
   * --------------------------- */
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setLoading(true);
      setSuccessMessage("");
      setErrorMessage("");

      try {
        const clean = buildCleanPayload(formData);
        console.log("PUT payload:", clean);
        await axios.put(`${apiUrl}/${id}`, clean);
        setSuccessMessage("Product updated successfully!");
      } catch (error) {
        console.error(
          "Error updating product:",
          error?.response?.data || error
        );
        setErrorMessage(
          error?.response?.data?.message ||
            "Update failed. Check required fields and types."
        );
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, id, formData]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit(e);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSubmit]);

  /* -----------------------------
   * Render
   * --------------------------- */
  if (fetching) return <p>Loading product data...</p>;

  return (
    <div className="dynamic-form">
      {errorMessage && (
        <div className="error-banner" role="alert">
          {String(errorMessage)}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {fields.map((fieldObj, index) => {
          const fieldName = fieldObj.fields;
          const isArrayField = !!fieldObj.subfields;

          return (
            <div key={index} className="form-group">
              <label>{fieldName}</label>

              {isArrayField ? (
                <div>
                  {(formData[fieldName] || []).map((item, itemIndex) => (
                    <div key={itemIndex} className="array-item">
                      {fieldObj.subfields.map((subField, subIndex) => (
                        <div key={subIndex} className="form-group">
                          <label>{subField.fields}</label>
                          <input
                            type="text"
                            name={`${fieldName}-${itemIndex}-${subField.fields}`}
                            value={item?.[subField.fields] || ""}
                            onChange={(e) =>
                              handleArrayChange(
                                fieldName,
                                itemIndex,
                                subField.fields,
                                e.target.value
                              )
                            }
                            required
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(fieldName, itemIndex)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => handleAddItem(fieldName)}
                  >
                    Add {fieldName}
                  </button>
                </div>
              ) : (
                // Simple input handling (with "projects" CSV support)
                <>
                  {fieldName === "id" ? (
                    <input
                      type="number"
                      name={fieldName}
                      value={
                        formData[fieldName] === undefined
                          ? ""
                          : String(formData[fieldName])
                      }
                      onChange={handleInputChange}
                      required
                    />
                  ) : fieldName === "projects" ? (
                    <input
                      type="text"
                      name="projects"
                      // If array, show CSV. If string, show as-is.
                      value={
                        Array.isArray(formData.projects)
                          ? formData.projects.join(",")
                          : formData.projects ?? ""
                      }
                      onChange={handleInputChange}
                      placeholder="e.g., 143,147,150"
                      required
                    />
                  ) : (
                    <input
                      type="text"
                      name={fieldName}
                      value={formData[fieldName] || ""}
                      onChange={handleInputChange}
                      required
                    />
                  )}
                </>
              )}
            </div>
          );
        })}

        <div className="buttoneditPage">
          <button type="submit" disabled={loading}>
            {loading ? "Updating..." : "Update Event"}
          </button>
        </div>
      </form>

      {successMessage && <p className="success-message">{successMessage}</p>}
    </div>
  );
};

export default DynamicEditForm;

